import React from 'react';
import { useTranslation } from 'react-i18next';
import ReportFooter from './ReportFooter';
import './ReportTaetigkeiten.css';

// Konstante: Zeilen pro Druckseite (Tätigkeiten Detail)
const ROWS_PER_PAGE = 24;

/**
 * Komponente für Tätigkeiten-Report (Kompakt oder Detail)
 */
function ReportTaetigkeiten({
  measurements,
  isKompakt,
  startPage = 2,
  totalPages = 2,
  decimalPlacesSollzeiten,
  formatTime,
  formatSollzeit,
  userEmail,
  userDeviceName
}) {
  const { t } = useTranslation();
  // Filtere und sortiere Messungen nach Activity (aufsteigend)
  const filteredMeasurements = measurements.filter(m => !m.isNote && !m.excluded);
  const sortedMeasurements = [...filteredMeasurements].sort((a, b) => {
    const activityA = (a.activity || '').toLowerCase();
    const activityB = (b.activity || '').toLowerCase();
    return activityA.localeCompare(activityB);
  });

  if (isKompakt) {
    // Kompakte Ansicht: Gruppiert nach Tätigkeit
    const groupedByActivity = {};
    sortedMeasurements.forEach(m => {
      const activity = m.activity || t('report.keine_taetigkeit');
      if (!groupedByActivity[activity]) {
        groupedByActivity[activity] = [];
      }
      groupedByActivity[activity].push(m);
    });

    const activities = Object.keys(groupedByActivity).sort();

    return (
      <div className="report-section report-taetigkeiten-kompakt">
        <h2 className="report-page-title" style={{ marginTop: '0', marginBottom: '15px', fontSize: '18px', fontWeight: 'bold', pageBreakAfter: 'avoid' }}>{t('report.taetigkeiten.kompakt.title')}</h2>
        {activities.map((activity, activityIndex) => {
          const activityMeasurements = groupedByActivity[activity];
          const totalTime = activityMeasurements.reduce((sum, m) => sum + (m.individualTime || 0), 0);
          const totalVorgabeTime = activityMeasurements.reduce((sum, m) => sum + (m.vorgabeTime || 0), 0);
          const count = activityMeasurements.length;

          // Berechne aktuelle Seite für diese Activity (vereinfacht: 1 Activity pro Seite nach der ersten)
          const currentActivityPage = activityIndex === 0 ? startPage : startPage + activityIndex;
          
          return (
            <div key={activity} style={{ marginBottom: '15px', pageBreakInside: 'avoid', position: 'relative', minHeight: activityIndex > 0 ? '297mm' : 'auto', paddingBottom: activityIndex > 0 ? '25mm' : '0' }}>
              {activityIndex > 0 && (
                <div className="report-page-number" style={{ position: 'absolute', bottom: '10mm', left: '50%', transform: 'translateX(-50%)', fontSize: '10pt', color: '#666', width: '100%', textAlign: 'center' }}>
                  {t('report.page_of', { page: currentActivityPage, total: totalPages })}
                </div>
              )}
              {activityIndex === 0 && (
                <div style={{ backgroundColor: '#f9fafb', padding: '8px', fontWeight: 'bold', borderBottom: '2px solid #ccc', marginBottom: '8px', pageBreakAfter: 'avoid' }}>
                  {(activity === t('report.keine_taetigkeit') ? t('report.keine_taetigkeit') : activity)} ({t('report.taetigkeiten.messpunkt_count', { count })})
                </div>
              )}
              {activityIndex > 0 && (
                <div className="report-activity-header" style={{ backgroundColor: '#f9fafb', padding: '8px', fontWeight: 'bold', borderBottom: '2px solid #ccc', marginBottom: '8px', pageBreakBefore: 'always', pageBreakAfter: 'avoid' }}>
                  {(activity === t('report.keine_taetigkeit') ? t('report.keine_taetigkeit') : activity)} ({t('report.taetigkeiten.messpunkt_count', { count })})
                </div>
              )}
              <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '10px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f0f0f0' }}>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center', width: '8%' }}>{t('report.header.nr')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center', width: '12%' }}>{t('report.header.zeitart')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center', width: '12%' }}>{t('report.header.leistungsgrad_short')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center', width: '12%' }}>{t('report.header.ist_hm')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center', width: '12%' }}>{t('report.header.soll_min')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'left', width: '44%' }}>{t('report.header.notiz')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activityMeasurements.map(m => (
                    <tr key={m.number}>
                      <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{m.number}</td>
                      <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{m.timeType || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{m.grade ? `${m.grade}%` : '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{formatTime(m.individualTime)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{m.vorgabeTime ? formatSollzeit(m.vorgabeTime, decimalPlacesSollzeiten) + ' ' + t('report.unit.min') : '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '5px' }}>{m.note || '-'}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#e5e7eb', fontWeight: 'bold' }}>
                    <td style={{ border: '1px solid #ccc', padding: '5px' }} colSpan="3">{t('report.summe')}</td>
                    <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{formatTime(totalTime)}</td>
                    <td style={{ border: '1px solid #ccc', padding: '5px', textAlign: 'center' }}>{formatSollzeit(totalVorgabeTime || 0, decimalPlacesSollzeiten)} {t('report.unit.min')}</td>
                    <td style={{ border: '1px solid #ccc', padding: '5px' }}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  } else {
    // Detail-Ansicht: Aufteilung in Chunks zu je 24 Zeilen pro Seite
    // Sortiere nach Nummer für konsistente Reihenfolge
    const sortedByNumber = [...filteredMeasurements].sort((a, b) => (a.number || 0) - (b.number || 0));
    
    // Teile in Chunks auf
    const chunks = [];
    for (let i = 0; i < sortedByNumber.length; i += ROWS_PER_PAGE) {
      chunks.push(sortedByNumber.slice(i, i + ROWS_PER_PAGE));
    }
    
    return (
      <div className="report-section report-taetigkeiten-detail">
        {chunks.map((chunk, chunkIndex) => {
          const isLastChunk = chunkIndex === chunks.length - 1;
          const currentPage = startPage + chunkIndex;
          
          return (
            <div 
              key={chunkIndex}
              className="report-print-page-block" 
              style={{ 
                position: 'relative', 
                minHeight: chunkIndex > 0 ? '297mm' : 'auto',
                paddingBottom: '25mm',
                paddingTop: chunkIndex > 0 ? '5mm' : '0',
                paddingLeft: '10mm',
                paddingRight: '10mm',
                pageBreakAfter: isLastChunk ? 'auto' : 'always'
              }}
            >
              <ReportFooter 
                pageNumber={currentPage} 
                totalPages={totalPages}
                userEmail={userEmail}
                userDeviceName={userDeviceName}
              />
              <h2 className="report-page-title" style={{ 
                marginTop: chunkIndex > 0 ? '0' : '0', 
                marginBottom: '15px', 
                fontSize: '18px', 
                fontWeight: 'bold', 
                pageBreakAfter: 'avoid',
                pageBreakBefore: chunkIndex > 0 ? 'always' : 'auto'
              }}>
                {chunkIndex === 0 ? t('report.taetigkeiten.detail.title') : t('report.taetigkeiten.detail.continuation')}
              </h2>
              <table className="report-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb' }}>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{t('report.header.nr')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>{t('report.header.taetigkeit')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{t('report.header.zeitart')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{t('report.header.leistungsgrad_short')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{t('report.header.ist_hm')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'center' }}>{t('report.header.soll_min')}</th>
                    <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>{t('report.header.notiz')}</th>
                  </tr>
                </thead>
                <tbody>
                  {chunk.map((m) => (
                    <tr key={m.number}>
                      <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{m.number}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px' }}>{m.activity || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{m.timeType || '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{m.grade ? `${m.grade}%` : '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{formatTime(m.individualTime)}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px', textAlign: 'center' }}>{m.vorgabeTime ? formatSollzeit(m.vorgabeTime, decimalPlacesSollzeiten) + ' ' + t('report.unit.min') : '-'}</td>
                      <td style={{ border: '1px solid #ccc', padding: '6px' }}>{m.note || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  }
}

export default ReportTaetigkeiten;
